import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Shield, UserPlus, Users } from "lucide-react";
import SignaturePad from "../shared/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_MATRIX = {
  low: { low: "green", medium: "yellow", high: "yellow" },
  medium: { low: "yellow", medium: "yellow", high: "red" },
  high: { low: "yellow", medium: "red", high: "red" },
};
const RISK_COLORS = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-red-500" };
const PPE_LABELS = { helmet: "🪖", harness: "🦺", gloves: "🧤", safety_shoes: "👢", hi_vis: "🦺", glasses: "🥽", hearing: "🎧", mask: "😷" };

// Multilingual UI chrome (job content stays as written by the author).
const LANGS = [
  { code: "no", flag: "🇳🇴", name: "Norsk" },
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "ro", flag: "🇷🇴", name: "Română" },
  { code: "pl", flag: "🇵🇱", name: "Polski" },
  { code: "lt", flag: "🇱🇹", name: "Lietuvių" },
];

const T = {
  no: { job: "Arbeidsbeskrivelse", hazards: "Farer", measures: "Tiltak", responsible: "Ansvarlig", low: "LAV", med: "MED", high: "HØY", ppe: "Påkrevd verneutstyr", emergency: "Beredskap", emg_contact: "Nødkontakt", hospital: "Sykehus", firstaid: "Førstehjelp", participants: "Deltagere", none_signed: "Ingen har signert ennå", sign_in: "Signer inn (jeg har lest og forstått SJA)", name: "Navn", company: "Firma", sign_here: "Signer her", add_sig: "Legg til signatur", signing: "Signerer...", finalize: "Ferdigstill SJA", finalizing: "Ferdigstiller...", signed: "signert", done_title: "Ferdigstilt!", done_msg: "SJA er signert av alle deltagere. PDF sendt til admin.", sja_no: "SJA nummer", have_signed: "deltagere har signert", fill_name: "Fyll ut navn", need_sig: "Signatur mangler", signed_by: "Signert av", confirm_finalize: "Ferdigstill SJA? PDF sendes til admin.", link_invalid: "Ugyldig lenke", sign_note: "Takk! Signaturen din er lagret. Den som opprettet SJA-en sender den inn fra appen.", error: "Feil" },
  en: { job: "Job description", hazards: "Hazards", measures: "Measures", responsible: "Responsible", low: "LOW", med: "MED", high: "HIGH", ppe: "Required PPE", emergency: "Emergency", emg_contact: "Emergency contact", hospital: "Hospital", firstaid: "First aid", participants: "Participants", none_signed: "No one has signed yet", sign_in: "Sign in (I have read and understood the SJA)", name: "Name", company: "Company", sign_here: "Sign here", add_sig: "Add signature", signing: "Signing...", finalize: "Finalize SJA", finalizing: "Finalizing...", signed: "signed", done_title: "Completed!", done_msg: "The SJA is signed by all participants. PDF sent to admin.", sja_no: "SJA number", have_signed: "participants have signed", fill_name: "Enter your name", need_sig: "Signature missing", signed_by: "Signed by", confirm_finalize: "Finalize SJA? PDF will be sent to admin.", link_invalid: "Invalid link", sign_note: "Thanks! Your signature is saved. The person who created the SJA will send it from the app.", error: "Error" },
  ro: { job: "Descrierea lucrării", hazards: "Pericole", measures: "Măsuri", responsible: "Responsabil", low: "MIC", med: "MED", high: "MARE", ppe: "Echipament de protecție", emergency: "Urgență", emg_contact: "Contact urgență", hospital: "Spital", firstaid: "Prim ajutor", participants: "Participanți", none_signed: "Nimeni nu a semnat încă", sign_in: "Semnează (am citit și înțeles SJA)", name: "Nume", company: "Firmă", sign_here: "Semnează aici", add_sig: "Adaugă semnătura", signing: "Se semnează...", finalize: "Finalizează SJA", finalizing: "Se finalizează...", signed: "semnat", done_title: "Finalizat!", done_msg: "SJA a fost semnat de toți participanții. PDF trimis la admin.", sja_no: "Număr SJA", have_signed: "participanți au semnat", fill_name: "Completează numele", need_sig: "Lipsește semnătura", signed_by: "Semnat de", confirm_finalize: "Finalizezi SJA? PDF-ul se trimite pe email.", link_invalid: "Link invalid", sign_note: "Mulțumim! Semnătura ta a fost salvată. Persoana care a inițiat SJA îl va trimite din aplicație.", error: "Eroare" },
  pl: { job: "Opis pracy", hazards: "Zagrożenia", measures: "Środki", responsible: "Odpowiedzialny", low: "NISKIE", med: "ŚR", high: "WYSOKIE", ppe: "Wymagane ŚOI", emergency: "Nagły wypadek", emg_contact: "Kontakt awaryjny", hospital: "Szpital", firstaid: "Pierwsza pomoc", participants: "Uczestnicy", none_signed: "Nikt jeszcze nie podpisał", sign_in: "Podpisz się (przeczytałem i zrozumiałem SJA)", name: "Imię", company: "Firma", sign_here: "Podpisz tutaj", add_sig: "Dodaj podpis", signing: "Podpisywanie...", finalize: "Zakończ SJA", finalizing: "Kończenie...", signed: "podpisano", done_title: "Zakończono!", done_msg: "SJA podpisane przez wszystkich uczestników. PDF wysłany do admina.", sja_no: "Numer SJA", have_signed: "uczestników podpisało", fill_name: "Wpisz imię", need_sig: "Brak podpisu", signed_by: "Podpisane przez", confirm_finalize: "Zakończyć SJA? PDF zostanie wysłany e-mailem.", link_invalid: "Nieprawidłowy link", sign_note: "Dziękujemy! Twój podpis został zapisany. Osoba, która utworzyła SJA, wyśle go z aplikacji.", error: "Błąd" },
  lt: { job: "Darbo aprašymas", hazards: "Pavojai", measures: "Priemonės", responsible: "Atsakingas", low: "MAŽA", med: "VID", high: "DIDELĖ", ppe: "Reikalingos AAP", emergency: "Avarinė", emg_contact: "Avarinis kontaktas", hospital: "Ligoninė", firstaid: "Pirmoji pagalba", participants: "Dalyviai", none_signed: "Dar niekas nepasirašė", sign_in: "Pasirašyti (perskaičiau ir supratau SJA)", name: "Vardas", company: "Įmonė", sign_here: "Pasirašykite čia", add_sig: "Pridėti parašą", signing: "Pasirašoma...", finalize: "Užbaigti SJA", finalizing: "Užbaigiama...", signed: "pasirašyta", done_title: "Užbaigta!", done_msg: "SJA pasirašė visi dalyviai. PDF išsiųstas administratoriui.", sja_no: "SJA numeris", have_signed: "dalyviai pasirašė", fill_name: "Įveskite vardą", need_sig: "Trūksta parašo", signed_by: "Pasirašė", confirm_finalize: "Užbaigti SJA? PDF bus išsiųstas el. paštu.", link_invalid: "Neteisinga nuoroda", sign_note: "Ačiū! Jūsų parašas išsaugotas. SJA sukūręs asmuo jį išsiųs iš programėlės.", error: "Klaida" },
};

export default function SJASign() {
  const { signToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("no");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("Doserne Stillas AS");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [finalized] = useState(false);
  const [tContent, setTContent] = useState(null); // translated free-text content
  const [translating, setTranslating] = useState(false);

  const t = (k) => (T[lang] || T.no)[k] || T.no[k] || k;

  // When the reader switches to a language other than the one the SJA was
  // created in, fetch the auto-translated content (cached server-side). The
  // stored SJA and the PDF stay in the original language.
  useEffect(() => {
    if (!data) return;
    const src = data.language || "no";
    if (!lang || lang === src) { setTContent(null); return; }
    let cancelled = false;
    setTranslating(true);
    axios.get(`${API}/sja/sign/${signToken}/i18n?lang=${lang}`)
      .then(({ data: r }) => { if (!cancelled) setTContent(r.content || null); })
      .catch(() => { if (!cancelled) setTContent(null); })
      .finally(() => { if (!cancelled) setTranslating(false); });
    return () => { cancelled = true; };
  }, [lang, data, signToken]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/sja/sign/${signToken}`);
      setData(data);
      if (data.language && T[data.language]) setLang(data.language);
    } catch (e) { toast.error(e.response?.data?.detail || "Link invalid"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [signToken]);

  const addParticipant = async () => {
    if (!name.trim()) { toast.error(t("fill_name")); return; }
    if (!signature) { toast.error(t("need_sig")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sja/sign/${signToken}/participant`, { name: name.trim(), company: company.trim(), signature });
      toast.success(`${t("signed_by")} ${name}`);
      setName("");
      setSignature(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">{t("link_invalid")}</div>;

  const LangBar = (
    <div className="flex items-center gap-1 flex-wrap">
      {LANGS.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)}
          className={`px-2 py-1 rounded-lg text-xs font-bold transition ${lang === l.code ? "bg-amber-400 text-slate-900" : "bg-white/10 text-white hover:bg-white/20"}`}
          data-testid={`sja-lang-${l.code}`} title={l.name}>
          {l.flag}
        </button>
      ))}
    </div>
  );

  const isDone = finalized || data.status === "finalized" || data.status === "sent";
  if (isDone) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-amber-50 to-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-lg">
            <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} style={{ color: "#fff" }} />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-2">{t("done_title")}</h1>
          <p className="text-slate-600 mb-6">{t("done_msg")}</p>
          <div className="bg-slate-900 rounded-xl p-4 shadow-md">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">{t("sja_no")}</div>
            <div className="text-4xl font-black text-amber-400 tracking-tight font-mono">{data.code}</div>
          </div>
          <p className="mt-4 text-xs text-slate-500">{data.participants?.length || 0} {t("have_signed")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="light-theme min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-16 text-slate-900">
      <div className="sticky top-0 z-40 bg-slate-900 text-white px-4 py-3 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-300" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Doserne Stillas AS</div>
            <h1 className="text-base font-bold" style={{ color: "#fff" }}>SJA {data.code}</h1>
          </div>
          {LangBar}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Job summary */}
        <div className="bg-amber-500 rounded-xl p-4 shadow-md" style={{ color: "#fff" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold mb-1 flex items-center gap-2">
            {t("job")}
            {translating && <Loader2 className="w-3 h-3 animate-spin" />}
          </div>
          <div className="text-lg font-bold" style={{ color: "#fff" }}>{tContent?.job_description ?? data.job_description}</div>
          <div className="text-xs mt-2" style={{ color: "#fff" }}>{data.work_location} — {data.work_date}</div>
        </div>

        {/* Hazards summary */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <h2 className="text-lg font-bold mb-3">{t("hazards")} ({data.hazards?.length || 0})</h2>
          <div className="space-y-2">
            {data.hazards?.map((h, i) => {
              const risk = RISK_MATRIX[h.probability]?.[h.consequence] || "yellow";
              const th = tContent?.hazards?.find((x) => x.i === i);
              return (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-bold text-sm">{th?.description || h.description}</div>
                    <span className={`${RISK_COLORS[risk]} px-2 py-0.5 text-xs font-bold rounded flex-shrink-0`} style={{ color: "#fff" }}>{risk === "green" ? t("low") : risk === "red" ? t("high") : t("med")}</span>
                  </div>
                  <div className="text-xs text-slate-600"><strong>{t("measures")}:</strong> {th?.preventive_measures || h.preventive_measures || "-"}</div>
                  {h.responsible && <div className="text-xs text-slate-500"><strong>{t("responsible")}:</strong> {h.responsible}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* PPE */}
        {data.ppe_required?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
            <h2 className="text-lg font-bold mb-3">{t("ppe")}</h2>
            <div className="flex flex-wrap gap-2">
              {data.ppe_required.map((p) => (
                <span key={p} className="px-3 py-1.5 bg-slate-900 text-white rounded-full text-sm font-semibold" style={{ color: "#fff" }}>{PPE_LABELS[p] || ""} {p}</span>
              ))}
            </div>
          </div>
        )}

        {/* Emergency */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-xs uppercase tracking-widest text-red-800 font-bold mb-2">{t("emergency")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div><strong>{t("emg_contact")}:</strong> {data.emergency_contact}</div>
            <div><strong>{t("hospital")}:</strong> {data.nearest_hospital || "-"}</div>
            <div><strong>{t("firstaid")}:</strong> {(tContent?.first_aid_location ?? data.first_aid_location) || "-"}</div>
          </div>
        </div>

        {/* Participants list */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2"><Users className="w-5 h-5 text-slate-500" /> {t("participants")} ({data.participants?.length || 0})</h2>
          </div>
          {data.participants?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {data.participants.map((p, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 bg-emerald-50" data-testid={`participant-${i}`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      {p.company && <div className="text-[10px] text-slate-500 truncate">{p.company}</div>}
                    </div>
                  </div>
                  {p.signature && <img src={p.signature} alt="" className="mt-1 w-full h-10 object-contain bg-white rounded" />}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 italic mb-4">{t("none_signed")}</p>}

          {/* Add participant */}
          <div className="border-t border-slate-200 pt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1"><UserPlus className="w-4 h-4" /> {t("sign_in")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${t("name")} *`}
                className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="participant-name" />
              <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder={t("company")}
                className="px-3 py-2 border border-slate-300 rounded-md text-slate-900 placeholder:text-slate-400" data-testid="participant-company" />
            </div>
            <SignaturePad value={signature} onChange={setSignature} label={t("sign_here")} />
            <button onClick={addParticipant} disabled={submitting || !name.trim() || !signature}
              className="w-full py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white rounded-md font-bold flex items-center justify-center gap-2"
              style={{ color: "#fff" }} data-testid="add-participant-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("signing")}</> : t("add_sig")}
            </button>
          </div>
        </div>

        {/* Signatures are collected here only. Sending the SJA is done by the
            person who initiated it, from inside the app (Project → SJA). */}
        {data.participants?.length > 0 && (
          <div className="w-full py-3 px-4 bg-slate-100 border border-slate-200 text-slate-600 rounded-md text-sm text-center" data-testid="sja-sign-note">
            {t("sign_note")}
          </div>
        )}
      </div>
    </div>
  );
}
